import { useState, useEffect } from 'react'
import { getLawDetail, getLawCompare, getLawTree } from '@/lib/api'
import type { SearchResult, LawDetail, LawArticle } from '@/types/law'

interface Props { result: SearchResult | null }
type ViewTab = 'text' | 'compare' | 'tree'

export default function LawViewer({ result }: Props) {
  const [activeTab, setActiveTab] = useState<ViewTab>('text')
  const [detail, setDetail] = useState<LawDetail | null>(null)
  const [compareData, setCompareData] = useState<unknown>(null)
  const [treeData, setTreeData] = useState<unknown>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!result || result.type !== 'law') { setDetail(null); return }
    setIsLoading(true); setError(null); setDetail(null)
    setCompareData(null); setTreeData(null); setActiveTab('text')
    getLawDetail(result.id)
      .then(setDetail)
      .catch((e: Error) => setError(e.message))
      .finally(() => setIsLoading(false))
  }, [result])

  const loadCompare = async () => {
    if (!result?.id || compareData) return
    try { setCompareData(await getLawCompare(result.id)) }
    catch (e) { setCompareData({ error: String(e) }) }
  }
  const loadTree = async () => {
    if (!result?.id || treeData) return
    try { setTreeData(await getLawTree(result.id)) }
    catch (e) { setTreeData({ error: String(e) }) }
  }
  const handleTabChange = (tab: ViewTab) => {
    setActiveTab(tab)
    if (tab === 'compare') loadCompare()
    if (tab === 'tree') loadTree()
  }

  if (!result) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 p-8">
        <div className="text-5xl mb-4">📜</div>
        <p className="font-medium text-gray-500">법령을 선택하면 전문이 표시됩니다</p>
        <p className="text-sm mt-1">검색 결과에서 법령을 클릭하세요.</p>
      </div>
    )
  }

  const info = detail?.법령?.기본정보
  const ministry = info?.소관부처
    ? (typeof info.소관부처 === 'object' ? info.소관부처.content : info.소관부처)
    : result.ministry

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h2 className="text-base font-bold text-gray-900 leading-snug">
            {info?.법령명_한글 ?? result.title}
          </h2>
          <a href={`https://www.law.go.kr/LSW/lsInfoP.do?lsiSeq=${result.id}`}
            target="_blank" rel="noopener noreferrer"
            className="flex-shrink-0 text-xs text-law-500 hover:text-law-700">
            법제처 ↗
          </a>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          {ministry && <span>📌 {ministry}</span>}
          {info?.시행일자 && <span>📅 시행 {formatDate(info.시행일자)}</span>}
          {info?.법령구분명 && <span>🏷 {info.법령구분명}</span>}
        </div>
      </div>

      {result.type === 'law' && (
        <div className="flex border-b border-gray-100 flex-shrink-0">
          {(['text','compare','tree'] as ViewTab[]).map((key) => (
            <button key={key} onClick={() => handleTabChange(key)}
              className={`flex-1 py-2.5 text-xs font-semibold transition-colors
                ${activeTab === key ? 'text-law-600 border-b-2 border-law-600' : 'text-gray-400 hover:text-gray-600'}`}>
              {key === 'text' ? '📄 조문 전문' : key === 'compare' ? '📊 신구 대조' : '🌳 위임 구조'}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && (
          <div className="space-y-4 animate-pulse">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i}>
                <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
                <div className="h-3 bg-gray-100 rounded mb-1" />
                <div className="h-3 w-5/6 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">
            <strong>오류:</strong> {error}
          </div>
        )}

        {!isLoading && !error && activeTab === 'text' && detail && (
          <LawArticles detail={detail} />
        )}

        {!isLoading && activeTab === 'compare' && (
          compareData
            ? <RawJson data={compareData} />
            : <Spinner text="신구법 대조 데이터를 불러오는 중..." />
        )}

        {!isLoading && activeTab === 'tree' && (
          treeData
            ? <RawJson data={treeData} />
            : <Spinner text="위임 구조 데이터를 불러오는 중..." />
        )}

        {result.type !== 'law' && (
          <div className="text-sm">
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-3">
              <p className="font-medium text-amber-700 mb-1">
                {result.type === 'prec' ? '⚖️ 판례' : '📋 행정규칙/기타'}
              </p>
              <p className="text-xs text-amber-600">전문 조회 기능은 준비 중입니다.</p>
              <a href="https://www.law.go.kr" target="_blank" rel="noopener noreferrer"
                className="mt-2 inline-block text-xs text-law-600 hover:underline">
                법제처 바로가기 ↗
              </a>
            </div>
            <RawJson data={result.raw} />
          </div>
        )}
      </div>
    </div>
  )
}

function LawArticles({ detail }: { detail: LawDetail }) {
  const rawArticles = detail.법령?.조문?.조문단위
  if (!rawArticles) return <p className="text-sm text-gray-400">조문 내용이 없습니다.</p>

  const articles: LawArticle[] = Array.isArray(rawArticles) ? rawArticles : [rawArticles]

  return (
    <div className="space-y-5 text-sm">
      {articles.map((article, idx) => {
        const isChapter = article.조문여부 === '전문' || !article.조문제목
        const paras = article.항 ? (Array.isArray(article.항) ? article.항 : [article.항]) : []

        if (isChapter) {
          return (
            <div key={idx} className="text-center py-2 mt-4 first:mt-0">
              <span className="text-xs font-bold text-gray-400 tracking-widest">
                {article.조문내용?.trim()}
              </span>
            </div>
          )
        }

        return (
          <div key={idx}>
            <h3 className="font-bold text-gray-800 mb-1.5 flex items-baseline gap-2">
              <span className="text-law-700">제{article.조문번호}조</span>
              {article.조문제목 && (
                <span className="font-normal text-gray-500 text-xs">({article.조문제목})</span>
              )}
            </h3>
            <div className="pl-3 border-l-2 border-gray-100 space-y-1.5">
              {paras.length === 0 && article.조문내용 && (
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {stripTitle(article.조문내용, article.조문번호)}
                </p>
              )}
              {paras.map((para, pi) => {
                const subs = para.호 ? (Array.isArray(para.호) ? para.호 : [para.호]) : []
                return (
                  <div key={pi}>
                    <p className="text-gray-700 leading-relaxed">{para.항내용}</p>
                    {subs.length > 0 && (
                      <div className="pl-4 mt-1 space-y-0.5">
                        {subs.map((sub, si) => (
                          <p key={si} className="text-gray-600 text-xs leading-relaxed">{sub.호내용}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function stripTitle(content: string, no: string): string {
  return content.replace(new RegExp(`^제${no}조(?:\\([^)]+\\))?\\s*`), '').trim()
}

function formatDate(date: string): string {
  if (date.length === 8) return `${date.slice(0,4)}.${date.slice(4,6)}.${date.slice(6,8)}`
  return date
}

function RawJson({ data }: { data: unknown }) {
  return (
    <pre className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-xl p-3 whitespace-pre-wrap overflow-auto max-h-96 font-mono">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}

function Spinner({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 text-gray-400 py-4 text-sm">
      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
      {text}
    </div>
  )
}
